import {
  Control,
  FieldValues,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { QueueRuleAction, Schema } from '@rossum/api-client/schemas';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { EmptyState } from '@rossum/components/empty-state/EmptyState';
import { Button, List, Stack } from '@rossum/ui/material';
import { button } from '../../../lib/formaterValues';
import { ActionForm } from './ActionForm';

type ThenSectionProps<T extends FieldValues> = {
  control: Control<T, unknown>;
  schema: Schema;
};

export const ThenSection = <T extends { actions: QueueRuleAction[] }>({
  control: parentControl,
  schema,
}: ThenSectionProps<T>) => {
  const intl = useIntl();

  // @ts-expect-error Parent control can have more fields but we will ignore them
  const control = parentControl as Control<
    { actions: QueueRuleAction[] },
    unknown
  >;

  const { getFieldState, formState } = useFormContext();

  const {
    fields: actions,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'actions',
  });

  const onAddAction = () => {
    append({
      id: uuidv4(),
      enabled: true,
      type: '',
      payload: {},
    });
  };

  return (
    <ContentGroup
      isFullWidthLayout
      description={intl.formatMessage({
        id: 'features.queueSettings.rules.detail.then.description',
      })}
      title={intl.formatMessage({
        id: 'features.queueSettings.rules.detail.then.title',
      })}
    >
      {actions.length > 0 ? (
        <List
          dense
          sx={{
            py: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {actions.map((action, index) => (
            <ActionForm
              remove={remove}
              control={control}
              key={action.id}
              action={action}
              index={index}
              schema={schema}
              getFieldState={getFieldState}
              disabled={formState.disabled}
            />
          ))}
        </List>
      ) : (
        <EmptyState
          title={intl.formatMessage({
            id: 'features.queueSettings.rules.detail.then.empty.title',
          })}
          description={intl.formatMessage(
            {
              id: 'features.queueSettings.rules.detail.then.empty.description',
            },
            {
              button: button(onAddAction),
            }
          )}
        />
      )}
      <Stack direction="column" alignItems="flex-end">
        <Button
          variant="outlined"
          color="secondary"
          onClick={onAddAction}
          disabled={formState.disabled}
          data-cy="add-action-button"
        >
          {intl.formatMessage({
            id: 'features.queueSettings.rules.detail.then.addAction',
          })}
        </Button>
      </Stack>
    </ContentGroup>
  );
};
